import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { VStack } from "@/shared/ui/Stack";
import { linkInputCoordinatesAtom, linkInputDefaultValueAtom } from "../AppStore";
import styles from "./LinkInput.module.css";
import { calculateInputCoordinates } from "./utils/calculateInputCoordinates";

export function LinkInput() {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const coordinates = useAtomValue(linkInputCoordinatesAtom);
    const defaultValue = useAtomValue(linkInputDefaultValueAtom);

    const handleInputFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const handleInputBlur = () => {
        if (!canv.textEditingController.getIsEditing()) {
            canv.appInterface.inputFocused = 0;
        }
    };

    const handleInputKeyDown = canv.appInterface.linkInputController.handleInputKeyDown;

    useEffect(() => {
        if (!coordinates || !wrapperRef.current) {
            return;
        }

        const wrapper = wrapperRef.current;
        const input = inputRef.current;
        const wrapperRect = wrapper.getBoundingClientRect();
        const canvasRect = canv.canvas.getBoundingClientRect();

        const { top, left } = calculateInputCoordinates(coordinates, wrapperRect, canvasRect);

        wrapper.style.transform = `translate3d(${left}px, ${top}px, 0)`;

        if (input && document.activeElement !== input) {
            input.focus();
        }
    }, [coordinates]);

    useEffect(() => {
        if (!coordinates) {
            return;
        }

        const handler = canv.appInterface.linkInputController.handleClickOutside(
            wrapperRef.current,
        );

        document.addEventListener("pointerdown", handler);

        return () => {
            document.removeEventListener("pointerdown", handler);
        };
    }, [coordinates]);

    if (coordinates) {
        return (
            <VStack justify="center" className={styles.wrapper} ref={wrapperRef}>
                <input
                    ref={inputRef}
                    className={styles.input}
                    defaultValue={defaultValue}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    placeholder={t("link.inputPlaceholder")}
                    data-ui-root="true"
                />
            </VStack>
        );
    }

    return null;
}
